import LinearProgress from "@material-ui/core/LinearProgress";
import {
  Brightness4Outlined,
  Brightness7Outlined,
  NotificationsNoneRounded,
} from "@material-ui/icons";
import AppContext from "context/AppContext";
import Cookies from "js-cookie";
import orderBy from "lodash/orderBy";
import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
import SideNavigation from "shared/components/SideNavigation/SideNavigation";
import { ReducerTypes } from "shared/types/reducerTypes";
import apiCalls from "shared/utils/apiCalls";
import ErrorMessage from "../ErrorMessage/ErrorMessage";
import Footer from "../Footer/Footer";
import Modal from "../Modal/Modal";
import SocialFooter from "../SocialFooter/SocialFooter";
import UserDropdown from "../UserDropdown/UserDropdown";
import styles from "./AppTemplate.module.scss";

const AppTemplate: FunctionComponent<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { state, dispatch } = useContext(AppContext);
  const [errorModalOpen, setErrorModalOpen] = useState(false);

  const history = useHistory();

  const getNotifications = async () => {
    const res = await apiCalls({
      method: "get",
      url: `/notifications`,
    });

    if (res) {
      dispatch({
        type: ReducerTypes.getNotification,
        payload: { value: res.data },
      });
    }
  };

  useEffect(() => {
    getNotifications();

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const storedTheme = getTheme();

    if (storedTheme) {
      dispatch({
        type: ReducerTypes.getTheme,
        payload: { value: storedTheme },
      });
      document.documentElement.setAttribute("data-theme", storedTheme);
    }

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const getChallenges = async () => {
      const res = await apiCalls(
        {
          method: "get",
          url: "/challenges",
        },
        dispatch
      );

      if (res) {
        dispatch({
          type: ReducerTypes.fetchChallenges,
          payload: {
            value: orderBy(res.data, ["level", "order"], ["asc", "asc"]),
          },
        });
      }
    };

    if (!state.challenges) {
      getChallenges();
    }

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setErrorModalOpen(!!state.error);
  }, [state.error]);

  const closeError = () => {
    setErrorModalOpen(false);

    dispatch({ type: ReducerTypes.clearError });
  };

  const getUnseenNotifications = () => {
    if (!state.notifications.notifications) {
      return 0;
    }

    return state.notifications.notifications.filter((notif: any) => !notif.seen)
      .length;
  };

  const switchTheme = () => {
    if (getTheme() === "light" || !getTheme()) {
      document.documentElement.setAttribute("data-theme", "dark");
      setTheme("dark");
      dispatch({
        type: ReducerTypes.getTheme,
        payload: { value: "dark" },
      });
    } else {
      document.documentElement.setAttribute("data-theme", "light");
      setTheme("light");
      dispatch({
        type: ReducerTypes.getTheme,
        payload: { value: "light" },
      });
    }
  };

  const setTheme = (theme: string) => {
    Cookies.set("theme", theme, { expires: 365 });
  };

  const getTheme = () => {
    return Cookies.get("theme");
  };

  const NotificationBell = () => {
    if (!state.user) {
      return <></>;
    }

    return (
      <button
        className={styles.appTemplate_header_notification}
        onClick={() => history.push("/notifications")}
        aria-label="notifications"
      >
        {!state.seenNotifications && getUnseenNotifications() > 0 && (
          <div className={styles.appTemplate_header_notification_newNumber}>
            {getUnseenNotifications()}
          </div>
        )}

        <div>
          <NotificationsNoneRounded style={{ minWidth: "24px" }} />
        </div>
      </button>
    );
  };

  const ThemeSwitcher = () => {
    return (
      <button
        className={styles.appTemplate_header_themeSwitcher}
        onClick={() => switchTheme()}
        aria-label="theme-switcher"
      >
        <div>
          {state.theme === "dark" ? (
            <Brightness7Outlined style={{ minWidth: "24px" }} />
          ) : (
            <Brightness4Outlined style={{ minWidth: "24px" }} />
          )}
        </div>
      </button>
    );
  };

  return (
    <div className={styles.appTemplate}>
      <SideNavigation
        notification={<NotificationBell />}
        themeSwitcher={<ThemeSwitcher />}
      />

      {state.pageLoading && (
        <div className={styles.appTemplate_pageLoader}>
          <LinearProgress color="primary" />
        </div>
      )}

      <div className={styles.appTemplate_contentWrapper} id="containerDiv">
        <div className={styles.appTemplate_children}>
          <div className={styles.appTemplate_header}>
            <ThemeSwitcher />

            {state.user && <NotificationBell />}

            <UserDropdown />
          </div>

          <Modal open={errorModalOpen} closeModal={closeError} size="sm">
            <ErrorMessage close={() => setErrorModalOpen(false)} />
          </Modal>

          {children}

          <SocialFooter />
        </div>

        <div className={styles.appTemplate_footer}>
          <Footer />
        </div>
      </div>
    </div>
  );
};

export default AppTemplate;
